<template>
  <div class="tabbed-configuration">
    <!-- Progress Indicator -->
    <div class="progress-container mb-6">
      <div class="flex items-center justify-between mb-2">
        <h2 class="text-xl font-semibold">
          {{ configuration ? 'Edit Configuration' : 'New Configuration' }}
        </h2>
        <div class="text-sm text-gray-600">
          {{ completedTabs }}/{{ totalTabs }} sections completed
        </div>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div 
          class="bg-blue-500 h-2 rounded-full transition-all duration-300"
          :style="{ width: `${(completedTabs / totalTabs) * 100}%` }"
        ></div>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation mb-6">
      <nav class="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.id"
          @click="setActiveTab(tab.id)"
          :disabled="!canAccessTab(tab.id)"
          class="tab-button flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
          :class="getTabClasses(tab)"
        >
          <div class="flex items-center justify-center space-x-2">
            <i :class="tab.icon" class="text-sm"></i>
            <span class="hidden sm:inline">{{ tab.label }}</span>
            <span class="sm:hidden">{{ tab.shortLabel }}</span>
            <div
              v-if="tab.status === 'completed'"
              class="w-2 h-2 bg-green-500 rounded-full"
            ></div>
            <div
              v-else-if="tab.status === 'error'"
              class="w-2 h-2 bg-red-500 rounded-full"
            ></div>
          </div>
        </button>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="tab-content bg-white rounded-lg shadow-sm border p-6">
      <!-- Basic Setup Tab -->
      <div v-if="activeTab === 'basic'" class="tab-panel">
        <BasicSetupTab
          v-model="form"
          :foundations="foundations"
          :templates="templates"
          @update:modelValue="onFormUpdate"
          @template-selected="onTemplateSelected"
        />
      </div>

      <!-- Connection Settings Tab -->
      <div v-if="activeTab === 'connection'" class="tab-panel">
        <ConnectionSettingsTab
          v-model="connectionSettings"
          @update:modelValue="onConnectionSettingsUpdate"
        />
      </div>

      <!-- Sync Settings Tab -->
      <div v-if="activeTab === 'sync'" class="tab-panel">
        <SyncSettingsTab
          v-model="syncSettings"
          @update:modelValue="onSyncSettingsUpdate"
        />
      </div>

      <!-- Field Mappings Tab -->
      <div v-if="activeTab === 'mappings'" class="tab-panel">
        <FieldMappingsTab
          v-model="fieldMappings"
          :templates="templates"
          :selected-template="form.template_id"
          @update:modelValue="onFieldMappingsUpdate"
        />
      </div>

      <!-- Test & Review Tab -->
      <div v-if="activeTab === 'review'" class="tab-panel">
        <TestReviewTab
          :configuration="configuration"
          :form-data="getAllFormData()"
          @test-connection="testConnection"
          @save-configuration="saveConfiguration"
          @cancel="$emit('cancel')"
        />
      </div>
    </div>

    <!-- Tab Navigation Controls -->
    <div class="flex justify-between mt-6">
      <button
        v-if="activeTabIndex > 0"
        @click="previousTab"
        class="btn btn-secondary flex items-center space-x-2"
      >
        <i class="fas fa-chevron-left"></i>
        <span>Previous</span>
      </button>
      <div v-else></div>

      <div class="flex space-x-3">
        <button
          @click="$emit('cancel')"
          class="btn btn-outline"
        >
          Cancel
        </button>
        
        <!-- Save button visible on all tabs -->
        <button
          @click="saveConfiguration"
          :disabled="!canSave()"
          class="btn btn-success flex items-center space-x-2"
        >
          <i v-if="saving" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-save"></i>
          <span>{{ configuration ? 'Update' : 'Create' }} Configuration</span>
        </button>
        
        <!-- Next button on all tabs except the last -->
        <button
          v-if="activeTabIndex < tabs.length - 1"
          @click="nextTab"
          :disabled="!canProceedToNext()"
          class="btn btn-primary flex items-center space-x-2"
        >
          <span>Next</span>
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import BasicSetupTab from './tabs/BasicSetupTab.vue';
import ConnectionSettingsTab from './tabs/ConnectionSettingsTab.vue';
import SyncSettingsTab from './tabs/SyncSettingsTab.vue';
import FieldMappingsTab from './tabs/FieldMappingsTab.vue';
import TestReviewTab from './tabs/TestReviewTab.vue';

export default {
  name: 'TabbedConfiguration',
  components: {
    BasicSetupTab,
    ConnectionSettingsTab,
    SyncSettingsTab,
    FieldMappingsTab,
    TestReviewTab,
  },
  props: {
    configuration: {
      type: Object,
      default: null,
    },
    foundations: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const activeTab = ref('basic');
    const saving = ref(false);

    // Form data for each section
    const form = ref({
      foundation_campaign_id: '',
      enabled: false,
      template_id: '',
    });

    const connectionSettings = ref({
      sync_mode: 'create_update',
      duplicate_check_field: 'email',
      use_assignment_rules: true,
      dry_run: false,
    });

    const syncSettings = ref({
      batch_size: 50,
      retry_attempts: 3,
      retry_delay: 60,
      log_level: 'info',
    });

    const fieldMappings = ref([]);

    // Tab configuration
    const tabs = ref([
      {
        id: 'basic',
        label: 'Basic Setup',
        shortLabel: 'Basic',
        icon: 'fas fa-cog',
        status: 'pending',
        required: true,
      },
      {
        id: 'connection',
        label: 'Connection Settings',
        shortLabel: 'Connection',
        icon: 'fas fa-plug',
        status: 'pending',
        required: false,
      },
      {
        id: 'sync',
        label: 'Sync Settings',
        shortLabel: 'Sync',
        icon: 'fas fa-sync',
        status: 'pending',
        required: false,
      },
      {
        id: 'mappings',
        label: 'Field Mappings',
        shortLabel: 'Mappings',
        icon: 'fas fa-exchange-alt',
        status: 'pending',
        required: true,
      },
      {
        id: 'review',
        label: 'Test & Review',
        shortLabel: 'Review',
        icon: 'fas fa-check-circle',
        status: 'pending',
        required: false,
      },
    ]);

    // Computed properties
    const activeTabIndex = computed(() => {
      return tabs.value.findIndex(tab => tab.id === activeTab.value);
    });

    const totalTabs = computed(() => tabs.value.length);

    const completedTabs = computed(() => {
      return tabs.value.filter(tab => tab.status === 'completed').length;
    });

    // Tab validation and status
    const getTabClasses = (tab) => {
      const isActive = activeTab.value === tab.id;
      const canAccess = canAccessTab(tab.id);
      
      if (isActive) {
        return 'bg-white text-blue-600 shadow-sm border border-blue-200';
      } else if (tab.status === 'completed') {
        return 'bg-green-50 text-green-700 hover:bg-green-100';
      } else if (tab.status === 'error') {
        return 'bg-red-50 text-red-700 hover:bg-red-100';
      } else if (canAccess) {
        return 'text-gray-700 hover:bg-gray-50';
      } else {
        return 'text-gray-400 cursor-not-allowed';
      }
    };

    const canAccessTab = (tabId) => {
      const tabIndex = tabs.value.findIndex(tab => tab.id === tabId);
      if (tabIndex === 0) return true; // Basic tab always accessible
      
      // Can access if all previous required tabs are completed
      for (let i = 0; i < tabIndex; i++) {
        const tab = tabs.value[i];
        if (tab.required && tab.status !== 'completed') {
          return false;
        }
      }
      return true;
    };

    const canProceedToNext = () => {
      const currentTab = tabs.value[activeTabIndex.value];
      return !currentTab.required || currentTab.status === 'completed';
    };

    const canSave = () => {
      return tabs.value.filter(tab => tab.required).every(tab => tab.status === 'completed');
    };

    // Tab navigation
    const setActiveTab = (tabId) => {
      if (canAccessTab(tabId)) {
        activeTab.value = tabId;
      }
    };

    const nextTab = () => {
      if (activeTabIndex.value < tabs.value.length - 1) {
        const nextTabId = tabs.value[activeTabIndex.value + 1].id;
        setActiveTab(nextTabId);
      }
    };

    const previousTab = () => {
      if (activeTabIndex.value > 0) {
        const prevTabId = tabs.value[activeTabIndex.value - 1].id;
        setActiveTab(prevTabId);
      }
    };

    // Form update handlers
    const onFormUpdate = (newData) => {
      // Direct update without debouncing since we fixed the circular update issue
      form.value = { ...form.value, ...newData };
      updateTabStatus('basic');
    };

    const onConnectionSettingsUpdate = (newData) => {
      connectionSettings.value = { ...connectionSettings.value, ...newData };
      updateTabStatus('connection');
    };

    const onSyncSettingsUpdate = (newData) => {
      syncSettings.value = { ...syncSettings.value, ...newData };
      updateTabStatus('sync');
    };

    const onFieldMappingsUpdate = (newData) => {
      fieldMappings.value = newData;
      updateTabStatus('mappings');
    };

    const onTemplateSelected = (template) => {
      if (template && template.mappings) {
        fieldMappings.value = JSON.parse(template.mappings);
        updateTabStatus('mappings');
      }
    };

    // Tab status validation
    const updateTabStatus = (tabId) => {
      const tab = tabs.value.find(t => t.id === tabId);
      if (!tab) return;

      switch (tabId) {
        case 'basic':
          tab.status = form.value.foundation_campaign_id ? 'completed' : 'pending';
          break;
        case 'connection':
          tab.status = 'completed'; // Connection settings have defaults
          break;
        case 'sync':
          tab.status = 'completed'; // Sync settings have defaults
          break;
        case 'mappings':
          tab.status = fieldMappings.value.length > 0 ? 'completed' : 'pending';
          break;
        case 'review':
          tab.status = canSave() ? 'completed' : 'pending';
          break;
      }
      
      // Always update review tab status when any other tab changes
      if (tabId !== 'review') {
        const reviewTab = tabs.value.find(t => t.id === 'review');
        if (reviewTab) {
          reviewTab.status = canSave() ? 'completed' : 'pending';
        }
      }
    };

    // Data aggregation
    const getAllFormData = () => {
      // Filter out incomplete field mappings
      const validMappings = (fieldMappings.value || []).filter(mapping => {
        // A mapping is valid if it has a target field and either a source field or mapping value
        return mapping.target_field && 
               (mapping.source_field || mapping.mapping_value || mapping.mapping_type === 'computed');
      });
      
      console.log('Filtering field mappings:', {
        original: fieldMappings.value.length,
        valid: validMappings.length
      });
      
      const data = {
        enabled: form.value.enabled,
        template_id: form.value.template_id,
        connection_settings: connectionSettings.value,
        sync_settings: syncSettings.value,
        field_mappings: validMappings,
      };
      
      // Include foundation_campaign_id for new configurations or duplications
      // For updates, only include if it's not an existing configuration with an ID
      if (!props.configuration || !props.configuration.id) {
        data.foundation_campaign_id = form.value.foundation_campaign_id;
      }
      
      return data;
    };

    // Actions
    const testConnection = async () => {
      // Pass through to parent or handle directly
      // The TestReviewTab will handle the actual API call
      return true;
    };

    const saveConfiguration = () => {
      saving.value = true;
      emit('save', getAllFormData());
    };

    // Initialize form with existing configuration
    onMounted(async () => {
      if (props.configuration) {
        console.log('TabbedConfiguration: Loading configuration', props.configuration);
        
        form.value = {
          foundation_campaign_id: String(props.configuration.foundation_campaign_id || ''),
          enabled: props.configuration.enabled || false,
          template_id: '',
        };

        if (props.configuration.connection_settings) {
          connectionSettings.value = {
            ...connectionSettings.value,
            ...props.configuration.connection_settings,
          };
        }

        if (props.configuration.sync_settings) {
          syncSettings.value = {
            ...syncSettings.value,
            ...props.configuration.sync_settings,
          };
        }

        // Handle both camelCase and snake_case property names
        console.log('TabbedConfiguration: Checking for field mappings...');
        console.log('  - fieldMappings:', props.configuration.fieldMappings);
        console.log('  - field_mappings:', props.configuration.field_mappings);
        
        if (props.configuration.fieldMappings || props.configuration.field_mappings) {
          fieldMappings.value = props.configuration.fieldMappings || props.configuration.field_mappings || [];
          console.log('TabbedConfiguration: Loaded field mappings count:', fieldMappings.value.length);
          if (fieldMappings.value.length > 0) {
            console.log('TabbedConfiguration: First mapping:', fieldMappings.value[0]);
          }
        } else {
          console.log('TabbedConfiguration: No field mappings found in configuration');
          fieldMappings.value = [];
        }

        // Wait for Vue to update the DOM
        await nextTick();

        // Update tab statuses based on existing data
        updateTabStatus('basic');
        updateTabStatus('connection');
        updateTabStatus('sync');
        updateTabStatus('mappings');
      }
    });

    // Watch for external saving completion
    watch(() => saving.value, (newVal) => {
      if (!newVal) {
        // Reset if needed
      }
    });

    return {
      activeTab,
      activeTabIndex,
      tabs,
      totalTabs,
      completedTabs,
      saving,
      form,
      connectionSettings,
      syncSettings,
      fieldMappings,
      getTabClasses,
      canAccessTab,
      canProceedToNext,
      canSave,
      setActiveTab,
      nextTab,
      previousTab,
      onFormUpdate,
      onConnectionSettingsUpdate,
      onSyncSettingsUpdate,
      onFieldMappingsUpdate,
      onTemplateSelected,
      getAllFormData,
      testConnection,
      saveConfiguration,
    };
  },
};
</script>

<style scoped>
.tab-button:disabled {
  @apply cursor-not-allowed opacity-50;
}

.tab-panel {
  @apply min-h-96;
}

.btn {
  @apply px-4 py-2 rounded font-medium transition-colors;
}

.btn-primary {
  @apply bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-secondary {
  @apply bg-gray-500 text-white hover:bg-gray-600;
}

.btn-success {
  @apply bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-outline {
  @apply border border-gray-300 text-gray-700 hover:bg-gray-50;
}

.progress-container {
  @apply bg-white rounded-lg shadow-sm border p-4;
}

.tab-navigation {
  @apply bg-white rounded-lg shadow-sm border p-2;
}

@media (max-width: 640px) {
  .tab-button {
    @apply px-2 py-2;
  }
  
  .tab-content {
    @apply p-4;
  }
}
</style>